import React, { useReducer, useState, useContext } from 'react';

// Services
import { getPollErrorsInputID } from '../../services/Poll';
import { getEnvironmentApiUrl } from '../../services/General';

// Context
import ScoresContext from '../../context/ScoresContext';

// Hooks
import { useToast } from '../../hooks/useToast';
import useHttp from '../../hooks/useHttp';

// Components
import CreatePoll from '../../components/CreatePollPage/CreatePoll/CreatePoll';
import ButtonsCreatePoll from '../../components/CreatePollPage/ButtonsCreatePoll/ButtonsCreatePoll';
import CreatePollToolbar from '../../components/CreatePollToolbar/CreatePollToolbar';

// Language 
import { useTranslation } from 'react-i18next';

// Interfaces
import { Poll } from '../../interfaces/Poll';

// Other
import { useLocation, useNavigate } from 'react-router-dom';
import { pollReducer } from '../../reducers/poll-reducers';
import { DEFAULT_DAILY_FREQ } from '../../data/ConstantsPolls';

const CreatePollPage: React.FC = () => {

    const { t } = useTranslation();
    const { scores, setScores } = useContext(ScoresContext);

    const location = useLocation();
    const oldPoll: Poll | undefined = location.state ? location.state.poll : undefined;

    const DEFAULT_POLL: Poll = {
        reminder_time: null,
        profile_ids: [],
        is_profesional_inquiry: false,
        ...DEFAULT_DAILY_FREQ,
        questions: [],
        has_table: false,
        show_in_charts: false,
        auto_call_enabled: false,
        validity_duration_hours: 24,
    };

    const navigate = useNavigate();

    const [pollState, dispatchPoll] = useReducer(pollReducer, { poll: oldPoll ?? DEFAULT_POLL, modified: false });
    const { poll, modified } = pollState;

    const [inputErrorIDs, setInputErrorIDs] = useState<string[]>([]);

    const toast = useToast();

    const { sendRequest: sendPoll } = useHttp<Poll>();


    const handleSavePoll = (redirect?: string, callbackValidationErr?: () => void) => {

        const errorInputIDs = getPollErrorsInputID(poll);
        if (errorInputIDs.length > 0) {
            setInputErrorIDs(errorInputIDs);
            if (callbackValidationErr) callbackValidationErr();
            return;
        }

        const pollWithoutUUID = {
            ...poll, questions: poll.questions?.map(question => ({
                ...question,
                uuid: undefined,
                answers: question.answers.map(answer => ({
                    ...answer, uuid: undefined
                }))
            }))
        };

        delete pollWithoutUUID.inquiry_alerts;

        if (pollState.poll.id === undefined) sendPoll({
            url: getEnvironmentApiUrl() + 'inquiry',
            method: 'POST',
            body: { ...pollWithoutUUID, deleted: false, enabled: true },
        }, (inquiry: Poll) => {
            dispatchPoll({ type: 'UPDATE_POLL', payload: inquiry });
            toast.success(t('poll_creation_success'));
            if (redirect) navigate(redirect);
        }, () => toast.error(t('poll_creation_error')));
        else sendPoll({
            url: getEnvironmentApiUrl() + 'inquiry/' + poll.id,
            method: 'PUT',
            body: { ...pollWithoutUUID, deleted: false, enabled: true, modified: false },
        }, (inquiry: Poll) => {
            dispatchPoll({ type: 'UPDATE_POLL', payload: inquiry });
            if (scores?.find(sc => sc.inquiry_id === poll.id)) // update inquiry_id in case inquiry has score
                setScores(oldScores => oldScores === null ? null : oldScores.map(sc => sc.inquiry_id !== poll.id ? sc : { ...sc, inquiry_id: inquiry.id }));
            toast.success(t('poll_update_success'));
            if (redirect) navigate(redirect);
        }, () => toast.error(t('poll_update_error')));

    };


    return (
        <div className='pageContainer'>
            <CreatePollToolbar
                modified={modified}
                handleSavePoll={handleSavePoll}
            />
            <CreatePoll
                poll={poll}
                create={oldPoll === undefined}
                inputErrorIDs={inputErrorIDs}
                dispatchPoll={dispatchPoll}
            >
                <ButtonsCreatePoll
                    poll={poll}
                    modified={modified}
                    dispatchPoll={dispatchPoll}
                    handleSave={handleSavePoll}
                />
            </CreatePoll>
        </div>
    );
};
export default CreatePollPage;